/**
 * Video Call Add-on System
 * สำหรับร้านค้าที่ต้องการเพิ่ม Video Call features
 * หรือร้านที่ใช้ Package Free/Basic
 */

/**
 * Video Call Add-on Types
 */
export const VIDEO_CALL_ADDON_TYPES = {
  BASIC: 'video_call_basic',
  PREMIUM: 'video_call_premium',
  UNLIMITED: 'video_call_unlimited'
};

/**
 * Video Call Add-on Definitions
 */
export const VIDEO_CALL_ADDONS = {
  [VIDEO_CALL_ADDON_TYPES.BASIC]: {
    id: VIDEO_CALL_ADDON_TYPES.BASIC,
    name: 'Video Call Basic',
    displayName: 'Video Call Basic',
    price: 199, // บาท/เดือน
    pricePerMonth: 199,
    features: {
      callsPerMonth: 10, // 10 calls/เดือน
      durationPerCall: 15, // 15 นาที/call
      recording: false,
      screenShare: true,
      priority: false
    },
    description: 'เหมาะสำหรับร้านที่ต้องการทดลอง Video Call',
    popular: true,
    isActive: true
  },
  
  [VIDEO_CALL_ADDON_TYPES.PREMIUM]: {
    id: VIDEO_CALL_ADDON_TYPES.PREMIUM,
    name: 'Video Call Premium',
    displayName: 'Video Call Premium',
    price: 399, // บาท/เดือน
    pricePerMonth: 399,
    features: {
      callsPerMonth: 30, // 30 calls/เดือน
      durationPerCall: 20, // 20 นาที/call
      recording: true,
      screenShare: true,
      priority: true
    },
    description: 'เหมาะสำหรับร้านที่ใช้ Video Call บ่อย',
    popular: false,
    isActive: true
  },
  
  [VIDEO_CALL_ADDON_TYPES.UNLIMITED]: {
    id: VIDEO_CALL_ADDON_TYPES.UNLIMITED,
    name: 'Video Call Unlimited',
    displayName: 'Video Call Unlimited',
    price: 799, // บาท/เดือน
    pricePerMonth: 799,
    features: {
      callsPerMonth: -1, // ไม่จำกัด
      durationPerCall: 30, // 30 นาที/call
      recording: true,
      screenShare: true,
      priority: true
    },
    description: 'เหมาะสำหรับร้านที่ใช้ Video Call อย่างต่อเนื่อง',
    popular: false,
    isActive: true
  }
};

/**
 * Get all active add-ons
 */
export const getActiveAddons = () => {
  return Object.values(VIDEO_CALL_ADDONS).filter(addon => addon.isActive);
};

/**
 * Get add-on by ID
 */
export const getAddonById = (addonId) => {
  return VIDEO_CALL_ADDONS[addonId] || null;
};

/**
 * Create Video Call Add-on Subscription Model
 */
export const createVideoCallAddonModel = (data) => {
  return {
    storeId: data.storeId || '',
    addonId: data.addonId || '',
    status: data.status || 'active', // active, expired, cancelled
    startDate: data.startDate || new Date(),
    endDate: data.endDate || null, // null = unlimited
    autoRenew: data.autoRenew || false,
    paymentMethod: data.paymentMethod || '', // bank, credit_card, etc.
    transactionId: data.transactionId || '',
    callsUsed: data.callsUsed || 0, // จำนวน calls ที่ใช้ไปแล้ว
    callsRemaining: data.callsRemaining || 0, // จำนวน calls ที่เหลือ
    createdAt: data.createdAt || new Date(),
    updatedAt: new Date()
  };
};

/**
 * Check if store has Video Call from Package
 */
export const hasVideoCallFromPackage = (packageType, packageFeatures) => {
  if (!packageFeatures) {
    const { getPackageFeatures } = require('./packageModels');
    packageFeatures = getPackageFeatures(packageType);
  }
  return packageFeatures?.videoCallEnabled === true;
};

/**
 * Get Video Call limit from Package
 */
export const getVideoCallLimitFromPackage = (packageType, packageFeatures) => {
  if (!packageFeatures) {
    const { getPackageFeatures } = require('./packageModels');
    packageFeatures = getPackageFeatures(packageType);
  }
  return packageFeatures?.videoCallLimit || 0;
};

/**
 * Get total Video Call limit (Package + Add-on)
 */
export const getTotalVideoCallLimit = (packageType, addonId, packageFeatures) => {
  let packageLimit = 0;
  
  // Get limit from package
  if (hasVideoCallFromPackage(packageType, packageFeatures)) {
    packageLimit = getVideoCallLimitFromPackage(packageType, packageFeatures);
  }
  
  // Get limit from add-on
  let addonLimit = 0;
  if (addonId) {
    const addon = getAddonById(addonId);
    if (addon) {
      addonLimit = addon.features.callsPerMonth;
    }
  }
  
  // If either is unlimited (-1), return unlimited
  if (packageLimit === -1 || addonLimit === -1) {
    return -1;
  }
  
  // Otherwise, sum them up
  return packageLimit + addonLimit;
};

/**
 * Check if Video Call is enabled (from Package or Add-on)
 */
export const isVideoCallEnabled = (packageType, addonId, packageFeatures) => {
  // Check package
  if (hasVideoCallFromPackage(packageType, packageFeatures)) {
    return true;
  }
  
  // Check add-on
  if (addonId) {
    const addon = getAddonById(addonId);
    return addon !== null && addon.isActive;
  }
  
  return false;
};

/**
 * Get Video Call duration (from Package or Add-on)
 */
export const getVideoCallDuration = (packageType, addonId, packageFeatures) => {
  // Priority: Add-on > Package
  if (addonId) {
    const addon = getAddonById(addonId);
    if (addon) {
      return addon.features.durationPerCall;
    }
  }
  
  // Get from package
  if (!packageFeatures) {
    const { getPackageFeatures } = require('./packageModels');
    packageFeatures = getPackageFeatures(packageType);
  }
  
  return packageFeatures?.videoCallDuration || 0;
};

/**
 * Check if Recording is enabled (from Package or Add-on)
 */
export const isRecordingEnabled = (packageType, addonId, packageFeatures) => {
  // Priority: Add-on > Package
  if (addonId) {
    const addon = getAddonById(addonId);
    if (addon) {
      return addon.features.recording === true;
    }
  }
  
  // Get from package
  if (!packageFeatures) {
    const { getPackageFeatures } = require('./packageModels');
    packageFeatures = getPackageFeatures(packageType);
  }
  
  return packageFeatures?.videoCallRecording === true;
};

/**
 * Check if Screen Share is enabled (from Package or Add-on)
 */
export const isScreenShareEnabled = (packageType, addonId, packageFeatures) => {
  // Priority: Add-on > Package
  if (addonId) {
    const addon = getAddonById(addonId);
    if (addon) {
      return addon.features.screenShare === true;
    }
  }
  
  // Get from package
  if (!packageFeatures) {
    const { getPackageFeatures } = require('./packageModels');
    packageFeatures = getPackageFeatures(packageType);
  }
  
  return packageFeatures?.videoCallScreenShare === true;
};







